/**
 * Pure booking-flow rules shared by socket and Bokun consumers.
 */

import { ActivityBookingDetails } from './ActivityBookingDetails';

/**
 * Determines whether opening a booking must trigger a Bokun synchronization.
 * @param externalId Product identifier supplied by the booking channel.
 * @param channelType Booking channel type.
 * @param confirmationCode Bokun confirmation code.
 * @returns Whether all Bokun synchronization constraints are satisfied.
 */
export const shouldSyncBokunBooking = (
	externalId: string | null | undefined,
	channelType: string | null | undefined,
	confirmationCode: string | null | undefined,
): boolean => {
	const normalizedExternalId = externalId?.trim().toUpperCase() ?? '';
	const normalizedChannel = channelType?.trim().toUpperCase() ?? '';
	const normalizedConfirmationCode = confirmationCode?.trim() ?? '';

	return normalizedConfirmationCode.length > 0
		&& normalizedChannel === 'BACKEND'
		&& (normalizedExternalId.startsWith('TK2') || normalizedExternalId.startsWith('TK3'));
};

/**
 * Extracts a calendar date from ISO and SQL datetime representations.
 * @param value Datetime represented with either a `T` or space separator.
 * @returns The YYYY-MM-DD date, or null when the representation is invalid.
 */
export const getBookingDate = (value: string | null | undefined): string | null => {
	const match = value?.trim().match(/^(\d{4}-\d{2}-\d{2})(?:T|\s|$)/);
	return match?.[1] ?? null;
};

/**
 * Checks whether a booking belongs to the active server-side date range.
 * @param startDateTime Booking start datetime.
 * @param fromDate Inclusive lower date boundary.
 * @param toDate Inclusive upper date boundary.
 * @returns Whether the booking can be represented by the current query.
 */
export const isBookingInDateRange = (
	startDateTime: string | null | undefined,
	fromDate: string,
	toDate: string,
): boolean => {
	const bookingDate = getBookingDate(startDateTime);
	if (!bookingDate) return false;

	return (!fromDate || bookingDate >= fromDate) && (!toDate || bookingDate <= toDate);
};

type BookingGridRow = ActivityBookingDetails & { id: number };

/**
 * Determines whether a grid row represents the activity carried by a refresh event.
 * External booking identifiers are considered only when neither side has an activity identifier.
 * @param row Existing grid row.
 * @param booking Booking carried by the refresh event.
 * @returns Whether the refresh event targets the existing row.
 */
const matchesRefreshActivity = (
	row: BookingGridRow,
	booking: ActivityBookingDetails,
): boolean => {
	const rowActivityId = String(row.ExternalActivityId ?? '').trim();
	const bookingActivityId = String(booking.ExternalActivityId ?? '').trim();

	if (rowActivityId || bookingActivityId) {
		return Boolean(rowActivityId) && rowActivityId === bookingActivityId;
	}

	const rowBookingId = String(row.ExternalBookingId ?? '').trim();
	const refreshBookingId = String(booking.ExternalBookingId ?? '').trim();
	return Boolean(rowBookingId) && rowBookingId === refreshBookingId;
};

/**
 * Applies a canonical refresh event without creating duplicate grid records.
 * @param rows Current grid rows.
 * @param booking Complete booking received from `refresh_bookings`.
 * @param fromDate Inclusive lower date boundary.
 * @param toDate Inclusive upper date boundary.
 * @returns Updated rows, preserving the original array when the event is out of range.
 */
export const mergeBookingRefresh = (
	rows: BookingGridRow[],
	booking: ActivityBookingDetails,
	fromDate: string,
	toDate: string,
): BookingGridRow[] => {
	const rowIndex = rows.findIndex((row) => matchesRefreshActivity(row, booking));

	if (rowIndex >= 0) {
		if (!isBookingInDateRange(booking.StartDateTime, fromDate, toDate)) {
			return rows.filter((_, index) => index !== rowIndex);
		}
		const nextRows = [...rows];
		nextRows[rowIndex] = { ...nextRows[rowIndex], ...booking, id: booking.BookingId };
		return nextRows;
	}

	if (!isBookingInDateRange(booking.StartDateTime, fromDate, toDate)) return rows;
	return [{ ...booking, id: booking.BookingId }, ...rows];
};
